/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { OemActivityType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OemActivityProps {
  id: string;
  oemPlanId: string;
  time: Date | null;
  authorId: string | null;
  content: string | null;
  activityType: OemActivityType;
  priorStatus: string | null;
  currentStatus: string | null;
  oemPlanChangeRequestId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class OemActivityRecord extends Record<OemActivityProps>({
  id: null as any,
  oemPlanId: null as any,
  time: null as any,
  authorId: null as any,
  content: null as any,
  activityType: null as any,
  priorStatus: null as any,
  currentStatus: null as any,
  oemPlanChangeRequestId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    oemPlanId: string;
    time: string | number | Date | null;
    authorId: string | null;
    content: string | null;
    activityType: OemActivityType;
    priorStatus: string | null;
    currentStatus: string | null;
    oemPlanChangeRequestId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): OemActivityRecord {
    if (json == null) {
      throw new Error('OemActivityRecord.fromJS: parameter cannot be null');
    }
    return new OemActivityRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OemActivityJson.id',
      ),
      oemPlanId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'oemPlanId', '00000000-0000-0000-0000-000000000000'),
        'OemActivityJson.oemPlanId',
      ),
      time: json.time != null ? new Date(json.time) : null,
      authorId: json.authorId,
      content: json.content,
      activityType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'activityType', OemActivityType.UserNote),
        'OemActivityJson.activityType',
      ),
      priorStatus: json.priorStatus,
      currentStatus: json.currentStatus,
      oemPlanChangeRequestId: json.oemPlanChangeRequestId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    oemPlanId: string;
    time: string | number | Date | null;
    authorId: string | null;
    content: string | null;
    activityType: OemActivityType;
    priorStatus: string | null;
    currentStatus: string | null;
    oemPlanChangeRequestId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [OemActivityRecord, Error[]] {
    if (json == null) {
      throw new Error('OemActivityRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OemActivityRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OemActivityJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      oemPlanId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'oemPlanId', '00000000-0000-0000-0000-000000000000'),
            'OemActivityJson.oemPlanId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      time: tryOrDefault(
        () => (json.time != null ? new Date(json.time) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      authorId: tryOrDefault(
        () => json.authorId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      content: tryOrDefault(
        () => json.content,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      activityType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'activityType', OemActivityType.UserNote),
            'OemActivityJson.activityType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      priorStatus: tryOrDefault(
        () => json.priorStatus,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentStatus: tryOrDefault(
        () => json.currentStatus,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      oemPlanChangeRequestId: tryOrDefault(
        () => json.oemPlanChangeRequestId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: OemActivityProps) {
    super(props);
    if (props == null) {
      throw new Error('OemActivityRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OemActivityViewModelBase implements OemActivityProps {
  protected readonly _props: OemActivityProps;
  private _json?: OemActivityProps;

  get id(): string {
    return this._props.id;
  }
  get oemPlanId(): string {
    return this._props.oemPlanId;
  }
  get time(): Date | null {
    return this._props.time;
  }
  get authorId(): string | null {
    return this._props.authorId;
  }
  get content(): string | null {
    return this._props.content;
  }
  get activityType(): OemActivityType {
    return this._props.activityType;
  }
  get priorStatus(): string | null {
    return this._props.priorStatus;
  }
  get currentStatus(): string | null {
    return this._props.currentStatus;
  }
  get oemPlanChangeRequestId(): string | null {
    return this._props.oemPlanChangeRequestId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAssetChangeNote() {
    return this._props.activityType === OemActivityType.AssetChangeNote;
  }
  public get isPlanChangeNote() {
    return this._props.activityType === OemActivityType.PlanChangeNote;
  }
  public get isUserNote() {
    return this._props.activityType === OemActivityType.UserNote;
  }

  public isAnyActivityType(...oemActivityType: OemActivityType[]) {
    return oemActivityType.includes(this._props.activityType);
  }

  constructor(props: OemActivityProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        oemPlanId: this._props.oemPlanId,
        time: this._props.time,
        authorId: this._props.authorId,
        content: this._props.content,
        activityType: this._props.activityType,
        priorStatus: this._props.priorStatus,
        currentStatus: this._props.currentStatus,
        oemPlanChangeRequestId: this._props.oemPlanChangeRequestId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllOemActivityEntities = (state: AppState) =>
  state.entityStores.oemActivity.entities;

export const selectOemActivityById = (id: string) => (state: AppState) =>
  state.entityStores.oemActivity.entities.get(id);

export const selectOemActivityMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.oemActivity.meta.get(id);

export const selectOemActivityAndMetadataById = (id: string) =>
  createSelector(
    selectOemActivityById(id),
    selectOemActivityMetadataById(id),
    (oemActivity, meta) => ({ oemActivity, meta }),
  );

export function selectOemActivityEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllOemActivityEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, OemActivityRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectOemActivityEntitiesByOemPlanId(oemPlanId: string) {
  return createSelector(selectAllOemActivityEntities, (entities) =>
    entities.filter((e) => e.oemPlanId === oemPlanId).valueSeq(),
  );
}

export function selectOemActivityEntitiesByTime(time: Date) {
  return createSelector(selectAllOemActivityEntities, (entities) =>
    entities.filter((e) => e.time === time).valueSeq(),
  );
}

export function selectOemActivityEntitiesByAuthorId(authorId: string) {
  return createSelector(selectAllOemActivityEntities, (entities) =>
    entities.filter((e) => e.authorId === authorId).valueSeq(),
  );
}

export function selectOemActivityEntitiesByContent(content: string) {
  return createSelector(selectAllOemActivityEntities, (entities) =>
    entities.filter((e) => e.content === content).valueSeq(),
  );
}

export function selectOemActivityEntitiesByActivityType(activityType: OemActivityType) {
  return createSelector(selectAllOemActivityEntities, (entities) =>
    entities.filter((e) => e.activityType === activityType).valueSeq(),
  );
}

export function selectOemActivityEntitiesByPriorStatus(priorStatus: string) {
  return createSelector(selectAllOemActivityEntities, (entities) =>
    entities.filter((e) => e.priorStatus === priorStatus).valueSeq(),
  );
}

export function selectOemActivityEntitiesByCurrentStatus(currentStatus: string) {
  return createSelector(selectAllOemActivityEntities, (entities) =>
    entities.filter((e) => e.currentStatus === currentStatus).valueSeq(),
  );
}

export function selectOemActivityEntitiesByOemPlanChangeRequestId(oemPlanChangeRequestId: string) {
  return createSelector(selectAllOemActivityEntities, (entities) =>
    entities.filter((e) => e.oemPlanChangeRequestId === oemPlanChangeRequestId).valueSeq(),
  );
}
//#endregion Selectors
